import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  Clock,
  Lock,
  LogIn,
  Shield,
  UserPlus,
  X
} from "lucide-react";
import { getPublicIntake, getPublicTemplate, getNdaStatus } from "../../utils/api/endpoints/questionnaire";
import { IntakeFormComponent } from "../../components/scoping/IntakeFormComponent";
import NdaGateScreen from "../../components/scoping/NdaGateScreen";
import NdaModal from "../../components/scoping/NdaModal";
import NdaDownloadButton from "../../components/scoping/NdaDownloadButton";
import usePageTitle from "../../utils/hooks/usePageTitle";
import { useStoreSelector } from "../../utils/hooks/hooks";
import {
  IntakePageShell,
  IntakeLoadingState,
  intakeTokens
} from "../../components/scoping/intake-ui";

function getExpiryDetails(expiresAt: string | null | undefined): {
  urgency: "safe" | "warn" | "critical" | "expired" | "none";
  label: string;
} {
  if (!expiresAt) return { urgency: "none", label: "" };
  const msLeft = new Date(expiresAt).getTime() - Date.now();
  if (msLeft <= 0) return { urgency: "expired", label: "This link has expired." };
  
  const daysLeft = Math.floor(msLeft / 86_400_000);
  const hoursLeft = Math.floor((msLeft % 86_400_000) / 3_600_000);
  const minutesLeft = Math.floor((msLeft % 3_600_000) / 60_000);
  
  const urgency = daysLeft === 0 ? "critical" : daysLeft <= 3 ? "warn" : "safe";
  
  let label = "";
  if (daysLeft > 0) {
    label = daysLeft <= 3
      ? `${daysLeft}d ${hoursLeft}h remaining`
      : `${daysLeft} days remaining on this link`;
  } else {
    if (hoursLeft > 0) {
      label = `Expires in ${hoursLeft}h ${minutesLeft}m — submit soon`;
    } else {
      label = `Expires in ${minutesLeft}m — submit soon`;
    }
  }
  
  return { urgency, label };
}const AuthBridge: React.FC<{
  token: string;
  clientEmail?: string | null;
  dismissed: boolean;
  onDismiss: () => void;
  isAuthenticated: boolean;
}> = ({ token, clientEmail, dismissed, onDismiss, isAuthenticated }) => {
  const navigate = useNavigate();
  if (dismissed || isAuthenticated) return null;

  return (
    <div className="w-full mb-6 bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-sm animate-in fade-in duration-200">
      <div className="flex items-center gap-4 flex-1">
        <div className="w-10 h-10 rounded-lg bg-[#081549] text-white flex items-center justify-center shrink-0 shadow-sm shadow-[#081549]/20">
          <Check size={20} className="text-[#4ADE80]" strokeWidth={3} />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <h3 className="text-sm font-bold text-slate-900 leading-tight">Save progress &amp; resume anytime</h3>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Create an account or log in to save your progress. You can easily return and continue filling out this scoping questionnaire at any time directly from your dashboard.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          type="button"
          onClick={() => navigate(`/register/business?ref=intake&token=${token}`)}
          className="px-4 py-2 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-bold transition shadow-sm active:scale-95 flex items-center gap-1.5"
        >
          <UserPlus size={14} />
          Sign up
        </button>
        <button
          type="button"
          onClick={() => navigate(`/login?ref=intake&token=${token}`)}
          className="px-3.5 py-2 hover:bg-slate-50 text-[#081549] text-xs font-bold rounded-xl border border-slate-200 bg-white transition active:scale-95 flex items-center gap-1.5"
        >
          <LogIn size={14} />
          Log in
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="p-1.5 text-slate-400 hover:text-slate-650 hover:bg-slate-100 rounded-lg transition"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const LoggedInRedirectBanner: React.FC<{ isAuthenticated: boolean; status?: string; hasPriorRecords?: boolean }> = ({
  isAuthenticated,
  status,
  hasPriorRecords
}) => {
  const navigate = useNavigate();
  if (!isAuthenticated || status !== "DRAFT" || hasPriorRecords) return null;
  return (
    <div className={`w-full mb-6 ${intakeTokens.card} p-4 flex items-center gap-3 shadow-sm`}>
      <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 shrink-0">
        <Check size={18} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-900">You&apos;re already signed in</p>
        <p className="text-[11px] text-slate-500">Continue in your dashboard for the same form.</p>
      </div>
      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        className="shrink-0 px-3 py-1.5 bg-ctb-blue text-white text-xs font-semibold rounded-lg shadow-sm"
      >
        Dashboard
      </button>
    </div>
  );
};

const IntakeFormPage: React.FC = () => {
  const { token, publicToken } = useParams<{ token?: string; publicToken?: string }>();
  usePageTitle("Security Scoping Intake");
  const navigate = useNavigate();

  const [activeToken, setActiveToken] = useState<string | undefined>(token);
  const [isTemplate, setIsTemplate] = useState<boolean>(!!publicToken);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any | null>(null);
  const [bridgeDismissed, setBridgeDismissed] = useState(false);
  const [expiryDetails, setExpiryDetails] = useState<{
    urgency: "safe" | "warn" | "critical" | "expired" | "none";
    label: string;
  }>({ urgency: "none", label: "" });

  // ── NDA state ──────────────────────────────────────────────────────────────
  // After NDA is signed, we transition without a full page reload for smoother UX
  const [ndaStatus, setNdaStatus] = useState<"PENDING" | "CLIENT_SIGNED" | "SIGNED">("PENDING");
  const [ndaSignerName, setNdaSignerName] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [ndaSignedAt, setNdaSignedAt] = useState<string | null>(null);
  const [showNdaModalCaseB, setShowNdaModalCaseB] = useState(false);

  const isAuthenticated = useStoreSelector(
    (state) =>
      (state as any)?.user?.loginStatus === "signedIn" ||
      !!(state as any)?.user?.credentials?.userId ||
      !!(state as any)?.user?.details?.user_id
  );

  useEffect(() => {
    console.log("IntakeFormPage loaded! token =", token, "publicToken =", publicToken);
    console.log("IntakeFormPage: activeToken =", activeToken, "isTemplate =", isTemplate);
    console.log("IntakeFormPage: data =", data);
  }, [token, publicToken, activeToken, isTemplate, data]);

  const loadIntakeData = useCallback(async (options?: { silent?: boolean }) => {
    if (!token && !publicToken) {
      setError("Token is missing");
      setLoading(false);
      return;
    }
    try {
      if (!options?.silent) {
        setLoading(true);
      }
      if (publicToken) {
        const res = await getPublicTemplate(publicToken);
        setData({
          ...res,
          answers: {},
          readOnly: false,
          status: "DRAFT"
        });
        setIsTemplate(true);
      } else if (token) {
        const res = await getPublicIntake(token);

        // ── Fallback: fetch NDA text if required but not included in response ──
        let ndaTextFallback = res.nda_text || null;
        if (res.nda_required && !ndaTextFallback) {
          try {
            const ndaRes = await getNdaStatus(token);
            ndaTextFallback = ndaRes.nda_text;
          } catch (ndaErr) {
            console.warn("Could not fetch NDA text:", ndaErr);
          }
        }

        setData({ ...res, nda_text: ndaTextFallback });
        setActiveToken(token);
        setIsTemplate(false);
        // Sync local NDA state from response
        if (res.nda_required) {
          setNdaStatus(res.nda_status || "PENDING");
          setNdaSignerName(res.nda_signer_name || null);
          setNdaSignedAt(res.nda_signed_at || null);
        }
      }
    } catch (err: any) {
      console.error(err);
      setError(
        err.response?.status === 429
          ? "Too many requests. Please wait a moment and try again."
          : err.message ||
              "Failed to load the scoping intake session. The link might be invalid or expired."
      );
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [token, publicToken]);

  useEffect(() => {
    loadIntakeData();
  }, [loadIntakeData]);

  // Live real-time expiry countdown timer
  useEffect(() => {
    if (!data?.expires_at) {
      setExpiryDetails({ urgency: "none", label: "" });
      return;
    }

    const updateCountdown = () => {
      setExpiryDetails(getExpiryDetails(data.expires_at));
    };

    updateCountdown();
    const timer = setInterval(updateCountdown, 30000); // refresh every 30 seconds
    return () => clearInterval(timer);
  }, [data?.expires_at]);

  if (loading) {
    return <IntakeLoadingState />;
  }

  if (error || !data) {
    return (
      <IntakePageShell>
        <div className={`${intakeTokens.card} max-w-md w-full p-8 text-center space-y-5 shadow-lg border border-slate-200/50`}>
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 flex items-center justify-center text-rose-600 border border-rose-100">
            <AlertTriangle size={28} />
          </div>
          <h2 className="text-lg font-bold text-slate-900">Link unavailable</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            {error ||
              "We could not open this scoping link. Ask your specialist for a new one."}
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => loadIntakeData()}
              className={intakeTokens.btnPrimary + " w-full"}
            >
              Try again
            </button>
            <Link
              to="/login"
              className={intakeTokens.btnSecondary + " w-full text-center"}
            >
              Log in instead
            </Link>
          </div>
        </div>
      </IntakePageShell>
    );
  }

  if (data.isExpired) {
    return (
      <IntakePageShell>
        <div className={`${intakeTokens.card} max-w-sm w-full p-6 text-center space-y-4 shadow-lg border border-slate-200/50 animate-in fade-in zoom-in-95 duration-200`}>
          <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 flex items-center justify-center text-rose-600 border border-rose-100">
            <Clock size={24} />
          </div>
          <h2 className="text-base font-bold text-slate-900">Scoping Link Expired</h2>
          <p className="text-xs text-slate-400 leading-relaxed px-2">
            This scoping link has expired. Please contact your solutions specialist to generate a new active link.
          </p>
          <div className="flex flex-col gap-2 pt-1">
            <Link
              to="/login"
              className={intakeTokens.btnPrimary + " w-full text-center"}
            >
              Log in to Dashboard
            </Link>
          </div>
        </div>
      </IntakePageShell>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb] flex flex-col montserrat-font antialiased text-slate-800">
      
      {/* ── Premium Top Header ── */}
      <header className="sticky top-0 z-[50] w-full h-14 bg-white border-b border-slate-200/80 shadow-sm select-none">
        <div className="max-w-[1400px] w-full h-full mx-auto px-4 sm:px-6 flex items-center justify-between">
          
          {/* Left Side: Logo & Expiry status */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/ctb-logo-crop.svg" alt="Capture The Bug" className="h-9 w-auto shrink-0" />
              <div className="flex flex-col text-left leading-none">
                <span className="text-[13px] font-black text-slate-800 tracking-tight">Capture The Bug</span>
                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest mt-1">Scoping Questionnaire</span>
              </div>
            </div>

            {expiryDetails.urgency !== "none" && (
              <div className="hidden md:flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 bg-emerald-50/50 border border-emerald-100/80 px-3.5 py-1.5 rounded-full shrink-0">
                <Clock size={12} className="text-emerald-600" />
                <span>{expiryDetails.label}</span>
              </div>
            )}
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-3">
            {!isTemplate && (
              <div className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 border border-emerald-200/80 text-emerald-700 rounded-full text-[10px] font-bold">
                <Check size={11} strokeWidth={3} />
                <span>Your progress is autosaved</span>
              </div>
            )}
            {!isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/register/business?ref=intake&token=${activeToken || ""}`)}
                  className="px-3.5 py-1.5 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-black transition shadow-sm active:scale-95 flex items-center gap-1"
                >
                  <UserPlus size={12} />
                  Sign up
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/login?ref=intake&token=${activeToken || ""}`)}
                  className="px-3 py-1.5 hover:bg-slate-50 text-slate-650 text-xs font-bold rounded-xl transition active:scale-95 flex items-center gap-1"
                >
                  <LogIn size={12} />
                  Log in
                </button>
              </>
            ) : (
              data?.status === "DRAFT" && !data?.hasPriorRecords && (
                <button
                  type="button"
                  onClick={() => navigate("/dashboard")}
                  className="px-3.5 py-1.5 bg-[#081549] hover:bg-[#081549]/90 text-white rounded-xl text-xs font-black transition shadow-sm active:scale-95 flex items-center gap-1.5"
                >
                  <span>Go to Dashboard</span>
                </button>
              )
            )}
          </div>
          
        </div>
      </header>
 
      {/* ── Main Viewport Content ── */}
      <main className="flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-6 flex flex-col">
        {isTemplate && isAuthenticated ? (
          <div className="w-full bg-white border border-slate-200 rounded-3xl p-8 shadow-sm flex flex-col items-center justify-center text-center max-w-xl mx-auto my-12 space-y-6">
            <div className="w-14 h-14 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600">
              <Lock size={28} />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-black text-slate-900">Signed in as registered user</h2>
              <p className="text-sm text-slate-500 leading-relaxed">
                You are currently logged in. To manage your active programs, please visit your dashboard. If you need to start a new scoping session, please contact our team to generate a personalized scoping link just for you.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="px-6 py-3 bg-[#081549] hover:bg-[#081549]/90 text-white font-bold rounded-xl text-sm transition shadow-md active:scale-95 flex items-center gap-2"
            >
              Go to Dashboard
            </button>
          </div>
        ) : (
          <>
            <LoggedInRedirectBanner isAuthenticated={isAuthenticated} status={data?.status} hasPriorRecords={data?.hasPriorRecords} />

            {data.status === "DRAFT" && !data.readOnly && !isTemplate && !(data.nda_required && !data.nda_enabled_after_creation && ndaStatus === "PENDING") && (
              <AuthBridge
                token={activeToken || ""}
                clientEmail={data.client_email}
                dismissed={bridgeDismissed}
                onDismiss={() => setBridgeDismissed(true)}
                isAuthenticated={isAuthenticated}
              />
            )}

            {(activeToken || publicToken) && (
              <div className="w-full mb-3 flex items-center justify-between text-[10px] text-slate-400 select-none">
                <span className="flex items-center gap-1">
                  <Lock size={11} />
                  {isTemplate ? "Public intake template" : "Secure intake session"}
                </span>
                <span className="font-mono text-slate-500 bg-slate-100 px-2 py-0.5 rounded border border-slate-200/50">
                  {(activeToken || publicToken)!.slice(0, 8)}…
                </span>
              </div>
            )}

            {/* ── NDA Gate Logic ─────────────────────────────────────────── */}

            {/* ── CASE A: NDA required BEFORE form (not enabled after creation) ── */}
            {data.nda_required && !data.nda_enabled_after_creation && !isTemplate ? (
              <>
                {/* A1: Client hasn't signed yet — show full NDA gate */}
                {ndaStatus === "PENDING" && (
                  <NdaGateScreen
                    token={activeToken || ""}
                    clientName={data.client_name || "Client"}
                    ndaText={data.nda_text || ""}
                    alreadySigned={false}
                    ndaSource={data}
                    onSigned={async () => {
                      setNdaStatus("SIGNED");
                      await loadIntakeData({ silent: true });
                    }}
                    onContinue={undefined}
                  />
                )}

                {/* A3: Fully executed — show signed banner + form */}
                {ndaStatus === "SIGNED" && (
                  <>
                    <div className="w-full mb-4 flex items-center justify-between gap-3
                                   p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
                      <div className="flex items-center gap-2.5">
                        <CheckCircle2 size={18} className="text-emerald-500 shrink-0" />
                        <div>
                          <p className="text-xs font-bold text-emerald-800">NDA Fully Executed</p>
                          {ndaSignerName && (
                            <p className="text-[10px] text-emerald-600">Signed by {ndaSignerName}</p>
                          )}
                        </div>
                      </div>
                      <NdaDownloadButton
                        ndaSource={data}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg
                                   bg-white border border-emerald-200 text-emerald-750
                                   text-[10px] font-semibold hover:bg-emerald-50 transition shrink-0
                                   disabled:opacity-50 disabled:pointer-events-none"
                      />
                    </div>
                    <IntakeFormComponent
                      token={activeToken || publicToken || ""}
                      initialData={data}
                      isDark={false}
                      isPublicTemplate={isTemplate}
                      publicToken={publicToken}
                      onDraftCreated={(newShareToken) => {
                        setActiveToken(newShareToken);
                        setIsTemplate(false);
                        if (!publicToken) {
                          navigate(`/intake/${newShareToken}`, { replace: true });
                        }
                      }}
                      onComplete={() => {
                        setData((prev: any) => ({
                          ...prev,
                          status: "SUBMITTED",
                          readOnly: true
                        }));
                      }}
                    />
                  </>
                )}
              </>
            ) : (
              /* ── CASE B: NDA enabled after creation, or NDA not required ── */
              <>
                {/* B1: NDA required but not yet signed — amber warning banner, allow form filling */}
                {data.nda_required && ndaStatus === "PENDING" && data.nda_enabled_after_creation && !isTemplate && (
                  <div className="w-full mb-4 flex items-start justify-between gap-3
                                 p-3.5 rounded-xl bg-amber-50 border border-amber-200">
                    <div className="flex items-start gap-2.5">
                      <Shield size={16} className="text-amber-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-bold text-amber-800">NDA Signature Required Before Submitting</p>
                        <p className="text-[10px] text-amber-700 mt-0.5 leading-relaxed">
                          Your specialist has enabled an NDA for this session. You can continue filling in the form,
                          but you must sign the NDA before you can submit.
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowNdaModalCaseB(true)}
                      className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg
                                 bg-amber-600 text-white text-[10px] font-bold
                                 hover:bg-amber-700 transition"
                    >
                      <Shield size={11} />
                      Sign NDA
                    </button>
                  </div>
                )}

                {/* B3: Fully executed banner */}
                {(ndaStatus === "SIGNED" || ndaStatus === "CLIENT_SIGNED") && data.nda_required && data.nda_enabled_after_creation && !isTemplate && (
                  <div className="w-full mb-4 flex items-center justify-between gap-3
                                 p-3.5 rounded-xl bg-emerald-50 border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={15} className="text-emerald-500 shrink-0" />
                      <p className="text-xs font-bold text-emerald-800">NDA Fully Executed — you may submit the form</p>
                    </div>
                    <NdaDownloadButton
                      ndaSource={data}
                      label="Download PDF"
                      className="inline-flex items-center gap-1 text-[10px] text-emerald-700 underline
                                 hover:text-emerald-800 shrink-0 disabled:opacity-50 disabled:no-underline
                                 disabled:pointer-events-none bg-transparent border-0 p-0 font-semibold"
                    />
                  </div>
                )}

                <IntakeFormComponent
                  token={activeToken || publicToken || ""}
                  initialData={data}
                  isDark={false}
                  isPublicTemplate={isTemplate}
                  publicToken={publicToken}
                  ndaRequired={data.nda_required && !isTemplate}
                  ndaStatus={ndaStatus}
                  ndaEnabledAfterCreation={data.nda_enabled_after_creation || false}
                  onNdaSignRequest={() => setShowNdaModalCaseB(true)}
                  onDraftCreated={(newShareToken) => {
                    setActiveToken(newShareToken);
                    setIsTemplate(false);
                    if (!publicToken) {
                      navigate(`/intake/${newShareToken}`, { replace: true });
                    }
                  }}
                  onComplete={() => {
                    setData((prev: any) => ({
                      ...prev,
                      status: "SUBMITTED",
                      readOnly: true
                    }));
                  }}
                />

                {/* Case B NDA Modal — opens when client clicks "Sign NDA" or tries to submit without signing */}
                {data.nda_required && !isTemplate && (
                  <NdaModal
                    isOpen={showNdaModalCaseB}
                    onClose={() => setShowNdaModalCaseB(false)}
                    onSigned={async (name, at) => {
                      setNdaStatus("SIGNED");
                      setNdaSignerName(name);
                      setNdaSignedAt(at);
                      setShowNdaModalCaseB(false);
                      await loadIntakeData({ silent: true });
                    }}
                    token={activeToken || ""}
                    clientName={data.client_name || "Client"}
                    ndaText={data.nda_text || ""}
                  />
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default IntakeFormPage;
